import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { deepCopy } from '../../../functions/deep-copy';
import { ControlConfig } from '../../../models/api/reports/report';
import { CasePipe } from '../../../pipes/case.pipe';
import { ControlDependencyService } from '../../../services/reports/control-dependency.service';
import { ProcessedControl } from '../../../services/reports/reports.service';
import { ControlValueHandler } from '../../../utilities/control-value-handler';
import { RadioButtonFieldComponent } from '../../core/fields/radio-button-field.component';
import { SelectFieldComponent } from '../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../core/labels/required-fields-note.component';
import { TagComponent } from '../../core/labels/tag.component';
import { BaseCriteriaComponent } from './base-criteria.component';

@Component({
  selector: 'app-report-output-options',
  imports: [
    PageHeaderComponent,
    SelectFieldComponent,
    RadioButtonFieldComponent,
    RequiredFieldsNoteComponent,
    TagComponent,
  ],
  providers: [CasePipe],
  template: `
    <app-page-header label="Report options" headerSize="h2">
      <ng-template #right>
        <app-required-fields-note classes="tw-pb-0 tw-mb-0" />
      </ng-template>
    </app-page-header>

    <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0 tw-overflow-hidden">
      @if (showReportName()) {
        <app-select-field
          [control]="fg.controls.reportName"
          labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%] sm:tw-max-w-[600px]"
          label="Report name to include"
          optionLabel="name"
          optionValue="code"
          [options]="reportNameOptions()"
          [filter]="false"
          [virtualScroll]="false"
          [showClear]="false"
          helpText="Report name to include on the report"
          [optionTemplate]="reportNameOption"
        />
        <ng-template #reportNameOption let-item="item">
          <div
            class="tw-flex tw-row-reverse tw-items-center tw-justify-between tw-w-full"
          >
            <span> {{ item.name }}</span>
            @if (item.code === 'UDRN') {
              <app-tag type="Grey">Report name</app-tag>
            } @else if (item.code === 'SDRN') {
              <app-tag type="Grey">Base report name</app-tag>
            }
          </div>
        </ng-template>
      }

      @if (showOutputFormat()) {
        <app-select-field
          [control]="fg.controls.outputFormat"
          label="Report format"
          labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%] sm:tw-max-w-[200px]"
          optionLabel="name"
          optionValue="code"
          [options]="outputFormatOptions()"
          [virtualScroll]="false"
          [showClear]="false"
          [filter]="false"
          [helpText]="getControl('OUTPUT_FORMAT')?.toolTipText"
        />
      }

      @if (showIncludeOptions()) {
        <app-select-field
          [control]="fg.controls.includeOnReport"
          labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%] sm:tw-max-w-[580px]"
          label="Fields to include on report header"
          optionLabel="name"
          optionValue="code"
          [optionDisabled]="'disabled'"
          [options]="includeOnReportOptions"
          [filter]="false"
          [multiple]="true"
          [virtualScroll]="false"
          [showHeader]="false"
          [chip]="true"
          [maxSelectedLabels]="4"
          [showClear]="false"
          [lockedOptions]="lockedIncludeOptions()"
          helpText="Select which information to include on the report header"
        />
      }

      @if (showAutofitXls()) {
        <app-radio-button-field
          [control]="fg.controls.autofitXls"
          label="Auto-fit column widths"
          labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%]"
          optionLabel="name"
          optionValue="code"
          [options]="autofitXlsOptions()"
          [vertical]="false"
          [disabled]="isAutofitDisabled()"
        />
      }

      @if (showPdfLogo()) {
        <app-radio-button-field
          [control]="fg.controls.pdfLogo"
          label="PDF logo selection"
          labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%]"
          optionLabel="name"
          optionValue="code"
          [options]="pdfLogoOptions()"
          [vertical]="false"
        />
      }
    </div>
  `,
})
export class ReportOutputOptionsComponent
  extends BaseCriteriaComponent
  implements OnInit
{
  private controlDependency = inject(ControlDependencyService);

  showReportName = signal(false);
  showOutputFormat = signal(false);
  showIncludeOptions = signal(false);
  showPdfLogo = signal(false);
  showAutofitXls = signal(false);

  private currentFormat = signal<string | null>(null);

  reportNameOptions = computed(() => {
    const options = this.controlConfig.getControlOptions('REPORT_NAME');
    const temp = deepCopy(options);
    const report = this.reports.currentReport();
    for (const option of temp) {
      if (option.code === 'UDRN') {
        option.name = report.reportInfo.requestName;
      } else if (option.code === 'SDRN') {
        option.name = report.reportTemplate.title;
      }
    }

    temp.reverse();
    return temp;
  });
  outputFormatOptions = computed(() => {
    // Report will have either OUTPUT_FORMAT or OUTPUT_FORMAT_PDF_ONLY, not both
    const options = [
      ...this.controlConfig.getControlOptions('OUTPUT_FORMAT'),
      ...this.controlConfig.getControlOptions('OUTPUT_FORMAT_PDF_ONLY'),
    ];
    if (this.reports.exceedsXlsFieldLimit()) {
      return options.filter((opt) => opt.code !== 'XLS');
    }
    return options;
  });
  pdfLogoOptions = computed(() => {
    const control = this.controlConfig.getControlByNames([
      'CLIENT_LOGO',
      'CLIENT_LOGO_INTERNAL',
    ]);
    return (
      control?.options?.map((opt) => ({ name: opt.name, code: opt.code })) || []
    );
  });
  autofitXlsOptions = computed(() =>
    this.controlConfig.getControlOptions('AUTOFIT_XLS'),
  );

  isAutofitDisabled = computed(() => this.currentFormat() === 'CSV');

  lockedIncludeOptions = computed(() => {
    const locked: string[] = [];
    this.INCLUDE_OPTION_ERF_NAMES.forEach((erfName) => {
      const control = this.controlConfig.getControl(erfName);
      if (!control) return;
      const isDisabled = this.evaluateDisabled(control);
      if (isDisabled && control.resetValueWhenDisabled === 'INCL') {
        locked.push(erfName);
      }
    });
    return locked;
  });

  includeOnReportOptions: {
    name: string;
    code: string;
    erfName: string;
    disabled: boolean;
  }[] = [];

  fg = new FormGroup<ReportOutputOptionsFormControls>({});

  protected readonly regionName = 'Output Options';

  protected CONTROL_CONFIGS: ControlConfig[] = [
    {
      erfNames: 'REPORT_NAME',
      formControlName: 'reportName',
      visibility: this.showReportName,
      transformValue: (val) => val || undefined,
    },
    {
      erfNames: ['OUTPUT_FORMAT', 'OUTPUT_FORMAT_PDF_ONLY'],
      formControlName: 'outputFormat',
      visibility: this.showOutputFormat,
      transformValue: (val) => val || undefined,
    },
    {
      erfNames: ['CLIENT_LOGO', 'CLIENT_LOGO_INTERNAL'],
      formControlName: 'pdfLogo',
      visibility: this.showPdfLogo,
      transformValue: (val) => val || undefined,
    },
    {
      erfNames: 'AUTOFIT_XLS',
      formControlName: 'autofitXls',
      visibility: this.showAutofitXls,
      transformValue: (val) => val || 'EXCL', // Default to "No"
    },
  ];

  private readonly INCLUDE_OPTION_ERF_NAMES = [
    'ACCOUNT_NAME',
    'REPORT_CRITERIA',
    'AS_OF_DATE_SELECT',
    'SCHEDULE_DETAILS_SELECT',
    'DISPLAY_CURRENCY',
    'TRANSACTION_DATE_SELECT',
    'TRANSACTION_DATE_SELECT_ACCESS_DATES',
  ];

  /** Controls that should be cleared (not sent) when hidden based on visibleWhen condition */
  private readonly CLEAR_WHEN_HIDDEN_ERF_NAMES = ['AUTOFIT_XLS'];

  constructor() {
    super();

    effect(() => {
      const isPdf = this.currentFormat() === 'PDF';
      const hasClientLogo = this.reports.clientHasCustomLogo();

      const shouldShow = isPdf && hasClientLogo;
      this.showPdfLogo.set(shouldShow);

      if (isPdf && !hasClientLogo) {
        // No custom logo - default to SEDGWICK_LOGO
        this.setClientLogoValue('SEDGWICK_LOGO');
      } else if (!isPdf) {
        // Not PDF - clear the value
        this.clearClientLogoValue();
      }
    });

    // Switch from XLS to XLSX when XLS becomes disabled due to field count
    effect(() => {
      if (this.reports.exceedsXlsFieldLimit()) {
        const outputFormatControl = this.fg.get('outputFormat');
        if (outputFormatControl?.value === 'XLS') {
          outputFormatControl.setValue('XLSX', { emitEvent: true });
        }
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadIncludeOptions();
    this.setupFormatChangeListener();
  }

  private loadIncludeOptions(): void {
    const options: {
      name: string;
      code: string;
      erfName: string;
      disabled: boolean;
    }[] = [];
    const selectedValues: string[] = [];

    this.INCLUDE_OPTION_ERF_NAMES.forEach((erfName) => {
      const control = this.controlConfig.getControl(erfName);

      if (control && control.region === 'Output Options') {
        // Check visibility condition from backend
        const isVisible = this.evaluateVisibility(control);
        if (!isVisible) {
          return;
        }

        // Check disabled condition
        const isDisabled = this.evaluateDisabled(control);

        options.push({
          name: control.label,
          code: erfName,
          erfName: erfName,
          disabled: isDisabled,
        });
        if (isDisabled) {
          // Check resetValueWhenDisabled to determine if we force select or deselect
          if (control.resetValueWhenDisabled === 'INCL') {
            // Force include when disabled (e.g., AS_OF_DATE_SELECT for PDF)
            selectedValues.push(erfName);
          }
          // else: don't include (EXCL) - do nothing
        } else if (control.defaultValue === 'INCL') {
          // Normal case: include based on defaultValue
          selectedValues.push(erfName);
        }
      }
    });

    if (options.length > 0) {
      this.includeOnReportOptions = options;
      this.showIncludeOptions.set(true);

      const includeControl = this.ensureFormControl<string[]>(
        'includeOnReport',
        [],
      );
      includeControl.setValue(selectedValues, {
        emitEvent: false,
      });
    }
  }

  private setupFormatChangeListener(): void {
    const outputFormatControl = this.fg.get('outputFormat');
    if (outputFormatControl) {
      this.currentFormat.set(outputFormatControl.value);
      this.updateIncludeOptionsVisibility();
      this.updateAutofitVisibility();

      this.dispose.subs = outputFormatControl.valueChanges.subscribe(
        (value) => {
          this.currentFormat.set(value);

          this.updateIncludeOptionsVisibility();
          this.updateAutofitVisibility();
        },
      );
    }

    // Guard to handle disabled options (prevent selecting EXCL ones, ensure INCL ones stay selected)
    const includeControl = this.fg.get('includeOnReport');
    if (includeControl) {
      this.dispose.subs = includeControl.valueChanges.subscribe((values) => {
        if (!values) return;

        let newValues = [...values];
        let hasChanges = false;

        this.INCLUDE_OPTION_ERF_NAMES.forEach((erfName) => {
          const control = this.controlConfig.getControl(erfName);
          if (!control) return;

          const isDisabled = this.evaluateDisabled(control);
          const isSelected = newValues.includes(erfName);

          if (isDisabled) {
            if (control.resetValueWhenDisabled === 'INCL') {
              // Force include - add if not selected
              if (!isSelected) {
                newValues.push(erfName);
                hasChanges = true;
              }
            } else {
              // Force exclude - remove if selected
              if (isSelected) {
                newValues = newValues.filter((v: string) => v !== erfName);
                hasChanges = true;
              }
            }
          }
        });

        if (hasChanges) {
          includeControl.setValue(newValues, { emitEvent: false });
        }
      });
    }
  }

  private clearClientLogoValue(): void {
    this.setClientLogoValue(undefined);
  }

  private setClientLogoValue(value: string | undefined): void {
    this.reports.currentReport.update((report) => {
      if (!report?.reportRequest?.controlsMap) return report;

      const controlName = report.reportRequest.controlsMap.has('CLIENT_LOGO')
        ? 'CLIENT_LOGO'
        : 'CLIENT_LOGO_INTERNAL';
      const clientLogoControl =
        report.reportRequest.controlsMap.get(controlName);
      if (clientLogoControl) {
        report.reportRequest.controlsMap.set(controlName, {
          ...clientLogoControl,
          defaultValue: value,
        });
      }
      return report;
    });
  }

  private updateIncludeOptionsVisibility(): void {
    const newOptions: {
      name: string;
      code: string;
      erfName: string;
      disabled: boolean;
    }[] = [];
    const includeControl = this.fg.get('includeOnReport');
    const currentValues = includeControl?.value || [];
    let newValues = [...currentValues];

    this.INCLUDE_OPTION_ERF_NAMES.forEach((erfName) => {
      const control = this.controlConfig.getControl(erfName);
      if (!control || control.region !== 'Output Options') return;

      const isVisible = this.evaluateVisibility(control);

      if (!isVisible) {
        // Remove from selected values when hidden
        newValues = newValues.filter((v: string) => v !== erfName);
      } else {
        // Check disabled condition
        const isDisabled = this.evaluateDisabled(control);

        // Add to options list
        newOptions.push({
          name: control.label,
          code: erfName,
          erfName: erfName,
          disabled: isDisabled,
        });
        if (isDisabled) {
          // Check resetValueWhenDisabled to determine if we force select or deselect
          if (control.resetValueWhenDisabled === 'INCL') {
            // Force include when disabled (e.g., AS_OF_DATE_SELECT for PDF)
            if (!newValues.includes(erfName)) {
              newValues.push(erfName);
            }
          } else {
            // Default behavior: remove from selected values when disabled
            newValues = newValues.filter((v: string) => v !== erfName);
          }
        }
      }
    });

    this.includeOnReportOptions = newOptions;

    if (includeControl) {
      includeControl.setValue(newValues, { emitEvent: false });
    }
  }

  private updateAutofitVisibility(): void {
    const format = this.currentFormat();
    // Show for XLS, XLSX, CSV; hide for PDF
    const isVisible = format === 'XLS' || format === 'XLSX' || format === 'CSV';
    this.showAutofitXls.set(isVisible);

    // When CSV is selected, force value to "No" (EXCL)
    if (format === 'CSV') {
      const autofitControl = this.fg.get('autofitXls');
      if (autofitControl && autofitControl.value !== 'EXCL') {
        autofitControl.setValue('EXCL', { emitEvent: false });
      }
    }
  }

  override reloadFormValues(): void {
    super.reloadFormValues();
    this.loadIncludeOptions();

    const outputFormatControl = this.fg.get('outputFormat');
    if (outputFormatControl) {
      this.currentFormat.set(outputFormatControl.value);

      this.updateIncludeOptionsVisibility();
      this.updateAutofitVisibility();
    }
  }

  override updateReport(): void {
    super.updateReport();

    this.reports.currentReport.update((report) => {
      if (!report.reportRequest?.controlsMap) return report;

      const controlsMap = report.reportRequest.controlsMap;

      const includeControl = this.fg.get('includeOnReport');
      const includeValues = includeControl?.value || [];
      this.INCLUDE_OPTION_ERF_NAMES.forEach((erfName) => {
        const control = this.controlConfig.getControl(erfName);
        if (!control || control.region !== 'Output Options') return;

        // Check if control is visible - only set values for visible controls
        // Hidden controls should not be sent (match CLASSIC behavior)
        const isVisible = this.evaluateVisibility(control);
        if (!isVisible) {
          // Clear value for hidden controls so they won't be sent
          if (controlsMap.has(erfName)) {
            controlsMap.set(erfName, {
              ...controlsMap.get(erfName)!,
              defaultValue: undefined,
            });
          }
          return;
        }

        const value = includeValues.includes(erfName) ? 'INCL' : 'EXCL';
        const formattedValue = ControlValueHandler.formatForApi(value);

        const updatedControl = {
          ...control,
          defaultValue: formattedValue,
        };

        if (formattedValue === undefined) {
          delete updatedControl.defaultValue;
        }
        controlsMap.set(erfName, updatedControl);
      });

      this.CLEAR_WHEN_HIDDEN_ERF_NAMES.forEach((erfName) => {
        const control = this.controlConfig.getControl(erfName);
        if (!control || !controlsMap.has(erfName)) return;

        const isVisible = this.evaluateVisibility(control);
        if (!isVisible) {
          controlsMap.set(erfName, {
            ...controlsMap.get(erfName)!,
            defaultValue: undefined,
          });
        }
      });

      return report;
    });
  }

  protected getControl(erfName: string): ProcessedControl | undefined {
    return this.controlConfig.getControl(erfName);
  }

  private evaluateVisibility(control: ProcessedControl): boolean {
    if (!control.visibleWhen) {
      return true; // No condition means always visible
    }
    return this.controlDependency.evaluateCondition(
      control.visibleWhen,
      this.fg,
    );
  }

  private evaluateDisabled(control: ProcessedControl): boolean {
    if (control.disabled) {
      return true; // Statically disabled
    }
    if (!control.disabledWhen) {
      return false; // No condition means not disabled
    }
    return this.controlDependency.evaluateCondition(
      control.disabledWhen,
      this.fg,
    );
  }
}

interface ReportOutputOptionsFormControls {
  reportName?: FormControl<string | null>;
  outputFormat?: FormControl<string | null>;
  includeOnReport?: FormControl<string[] | null>;
  pdfLogo?: FormControl<string | null>;
  autofitXls?: FormControl<string | null>;
}
